import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { useAuth } from "@/contexts/AuthContext";
import { EditPageLayout } from "@/components/shared/EditPageLayout";
import { FileUpload } from "@/components/shared/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  updateProfile,
  uploadProfileImage,
  type UpdateProfileParams,
} from "@/services/authService";

function getDisplayName(
  firstName: string | null,
  lastName: string | null,
  displayName: string | null,
  email: string
): string {
  const parts = [firstName, lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (displayName?.trim()) return displayName;
  return email;
}

export function ProfileEditPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(
    user?.profileImage ?? null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
    setProfileImageUrl(user?.profileImage ?? null);
  }, [user?.firstName, user?.lastName, user?.profileImage]);

  const handleCancel = useCallback(() => {
    navigate("/profile");
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    if (!user) return;
    setIsSaving(true);
    try {
      const payload: UpdateProfileParams = {};
      if (firstName.trim() !== (user.firstName ?? ""))
        payload.firstName = firstName.trim() || undefined;
      if (lastName.trim() !== (user.lastName ?? ""))
        payload.lastName = lastName.trim() || undefined;
      if (Object.keys(payload).length > 0) {
        await updateProfile(payload);
      }
      if (selectedFile != null) {
        await uploadProfileImage(selectedFile);
      }
      await refreshUser();
      setSelectedFile(null);
      toast.success(t("profile.saveSuccess", "Profile updated."));
      navigate("/profile");
    } catch {
      toast.error(t("profile.saveError", "Could not update profile. Please try again."));
    } finally {
      setIsSaving(false);
    }
  }, [user, firstName, lastName, selectedFile, refreshUser, navigate, t]);

  if (!user) {
    return (
      <EditPageLayout
        title={t("profile.title", "Profile")}
        description={t("profile.description", "Your account details")}
        isLoading={false}
        formId="profile-edit-form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        onCancel={handleCancel}
        cancelLabel={t("profile.cancel", "Cancel")}
        onSave={() => {}}
        saveLabel={t("profile.save", "Save")}
        savingLabel={t("profile.saving", "Saving…")}
        isSaving={false}
        entityExists={false}
      >
        <div className="w-full max-w-xl" />
      </EditPageLayout>
    );
  }

  return (
    <EditPageLayout
      title={getDisplayName(
        firstName || null,
        lastName || null,
        user.displayName ?? null,
        user.email
      )}
      description={t("profile.description", "Your account details")}
      isLoading={false}
      formId="profile-edit-form"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
      onCancel={handleCancel}
      cancelLabel={t("profile.cancel", "Cancel")}
      onSave={() => void handleSubmit()}
      saveLabel={t("profile.save", "Save")}
      savingLabel={t("profile.saving", "Saving…")}
      isSaving={isSaving}
      entityExists
    >
      <div className="w-full max-w-xl space-y-6">
        <div>
          <h3 className="mb-1 text-sm font-medium text-foreground">
            {t("profile.profilePicture", "Profile picture")}
          </h3>
          <p className="mb-2 text-xs text-muted-foreground">
            {t(
              "profile.profilePictureHelp",
              "Upload a profile picture. JPEG, PNG or WebP, max 5 MB."
            )}
          </p>
          <FileUpload
            currentImageUrl={profileImageUrl}
            onFileChange={setSelectedFile}
            disabled={isSaving}
            showLabel={false}
          />
        </div>
        <form id="profile-edit-form" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="profile-firstName">
                {t("profile.firstName", "First name")}
              </Label>
              <Input
                id="profile-firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-lastName">
                {t("profile.lastName", "Last name")}
              </Label>
              <Input
                id="profile-lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-email">{t("profile.email", "Email")}</Label>
            <Input
              id="profile-email"
              type="email"
              value={user.email}
              disabled
              className="bg-muted"
            />
          </div>
        </form>
      </div>
    </EditPageLayout>
  );
}
